import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/pages/Transcription";
import DictateButton from "@/components/DictateButton";
import { useCannedText } from "@/components/CannedText";
import {
  ArrowLeft, Save, SendHorizontal, Lock, PenLine, Undo2, Clock, FileText, History, Mic,
} from "lucide-react";

const Field = ({ label, value }) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-sm text-slate-800">{value || "—"}</div>
  </div>
);

export default function TranscriptionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const [row, setRow] = useState(null);
  const [gross, setGross] = useState("");
  const [dx, setDx] = useState("");
  const [note, setNote] = useState("");
  const [amendReason, setAmendReason] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [amending, setAmending] = useState(false);
  const [busy, setBusy] = useState("");
  const grossCanned = useCannedText("gross_description", gross, setGross);
  const dxCanned = useCannedText("diagnosis", dx, setDx);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/transcription/${id}`);
      setRow(data);
      setGross(data.gross_description || "");
      setDx(data.diagnosis || "");
      setNote(data.transcription_note || "");
    } catch (e) { toast.error(apiError(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!row) return <div className="p-6 text-sm text-slate-500">Loading report…</div>;

  const locked = row.locked;
  const canWrite = row.can_write;
  const canSignout = row.can_signout;

  const saveDraft = async () => {
    setBusy("save");
    try {
      await api.put(`/transcription/${id}`, { gross_description: gross, diagnosis: dx, transcription_note: note });
      toast.success("Draft saved");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const flagReady = async () => {
    setBusy("ready");
    try {
      await api.put(`/transcription/${id}`, { gross_description: gross, diagnosis: dx, transcription_note: note });
      await api.post(`/transcription/${id}/ready`, { note });
      toast.success("Flagged ready — the assigned pathologist has been notified");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const signOut = async () => {
    setBusy("signout");
    try {
      await api.post(`/cases/${id}/signout`, { diagnosis: dx, comments: row.comments || "" });
      toast.success("Report signed out and locked");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const sendBack = async () => {
    setBusy("return");
    try {
      await api.post(`/transcription/${id}/return`, { reason: returnReason });
      toast.success("Returned to transcription");
      setReturnReason("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const fileAmendment = async () => {
    setBusy("amend");
    try {
      await api.post(`/cases/${id}/amend`, { reason: amendReason, diagnosis: dx, comments: "" });
      toast.success("Amended report filed");
      setAmendReason("");
      setAmending(false);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="transcription-editor-page">
      <div className="flex flex-wrap items-start gap-3">
        <button onClick={() => navigate("/transcription")} data-testid="back-to-queue"
                className="inline-flex h-10 items-center gap-1 border border-slate-300 px-3 text-sm font-medium text-slate-700 hover:border-[#134e4a]">
          <ArrowLeft size={15} /> Queue
        </button>
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            <span className="font-mono">{row.accession_number}</span> — {row.patient_name}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <StatusPill status={row.report_status} />
            {row.priority === "STAT" && (
              <span className="bg-red-100 px-2 py-0.5 text-[11px] font-bold uppercase text-red-800" data-testid="editor-stat-flag">STAT</span>
            )}
            <span className={`inline-flex items-center gap-1 ${row.tat_breached ? "font-semibold text-red-700" : ""}`}
                  data-testid="editor-tat">
              <Clock size={13} /> TAT {row.tat_hours}h {row.tat_running ? "(running)" : "(stopped at sign-out)"}
            </span>
          </p>
        </div>
        <Link to={`/cases/${id}`} data-testid="editor-case-link"
              className="ml-auto inline-flex h-10 items-center gap-1 border border-slate-300 px-3 text-sm font-medium text-slate-700 hover:border-[#134e4a]">
          <FileText size={15} /> Case record
        </Link>
      </div>

      <section className="grid gap-4 border border-slate-200 p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="editor-case-facts">
        <Field label="Specimen type" value={row.specimen_type} />
        <Field label="Specimen site" value={row.specimen_site} />
        <Field label="Assigned pathologist" value={row.pathologist_name || "Unassigned"} />
        <Field label="Received" value={fmtDateTime(row.received_at)} />
        <Field label="MRN" value={row.patient_mrn} />
        <Field label="Typed by" value={row.transcribed_by_name} />
        <Field label="Last typed" value={row.transcription_updated_at ? fmtDateTime(row.transcription_updated_at) : "—"} />
        <Field label="Signed out" value={row.signed_out_at ? `${fmtDateTime(row.signed_out_at)} — ${row.report_signed_by}` : "Not signed out"} />
      </section>

      {locked && (
        <div className="flex items-center gap-2 border-l-4 border-emerald-600 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
             data-testid="editor-locked-banner">
          <Lock size={15} /> This report is signed out and read-only. Changes require a formal amendment.
        </div>
      )}

      <section className="space-y-4 border border-slate-200 p-4" data-testid="editor-report-fields">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Gross description</Label>
            {canWrite && (
              <DictateButton value={gross} onChange={setGross} field="gross_description" caseId={id}
                             testid="dictate-gross" label="Dictate gross" />
            )}
            {canWrite && grossCanned.trigger}
          </div>
          <Textarea ref={grossCanned.ref} value={gross} onChange={(e) => setGross(e.target.value)} rows={7}
                    onKeyDown={grossCanned.handleKeyDown}
                    disabled={!canWrite} data-testid="gross-description-input"
                    placeholder="Type or dictate the gross description… type .trigger then Tab for canned text"
                    className="mt-1 font-mono text-sm disabled:bg-slate-50" />
          {canWrite && grossCanned.panel}
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Final diagnosis</Label>
            {(canWrite || (locked && amending)) && (
              <DictateButton value={dx} onChange={setDx} field="diagnosis" caseId={id}
                             testid="dictate-diagnosis" label="Dictate diagnosis" />
            )}
            {(canWrite || (locked && amending)) && dxCanned.trigger}
          </div>
          <Textarea ref={dxCanned.ref} value={dx} onChange={(e) => setDx(e.target.value)} rows={7}
                    onKeyDown={dxCanned.handleKeyDown}
                    disabled={!canWrite && !(locked && amending)} data-testid="final-diagnosis-input"
                    placeholder="Type or dictate the final diagnosis… type .trigger then Tab for canned text"
                    className="mt-1 font-mono text-sm disabled:bg-slate-50" />
          {(canWrite || (locked && amending)) && dxCanned.panel}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Transcription note to the pathologist</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                    disabled={!canWrite} data-testid="transcription-note-input"
                    placeholder="Unclear audio, spelling to confirm, etc."
                    className="mt-1 text-sm disabled:bg-slate-50" />
        </div>

        {canWrite && (
          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            <Button onClick={saveDraft} disabled={busy === "save"} data-testid="save-draft-button"
                    className="h-11 bg-[#134e4a] hover:bg-[#0f3e3a]">
              <Save size={15} className="mr-2" /> {busy === "save" ? "Saving…" : "Save draft"}
            </Button>
            <Button onClick={flagReady} disabled={busy === "ready" || !dx.trim()} data-testid="flag-ready-button"
                    variant="outline" className="h-11 border-amber-500 text-amber-800 hover:bg-amber-50">
              <SendHorizontal size={15} className="mr-2" /> {busy === "ready" ? "Flagging…" : "Flag ready for review"}
            </Button>
          </div>
        )}
        {!canWrite && !locked && (
          <p className="border-t border-slate-100 pt-4 text-sm text-slate-500" data-testid="editor-readonly-note">
            Read-only for your role ({user?.role_label || user?.role}). Transcriptionists and pathologists edit report drafts.
          </p>
        )}
      </section>

      {canSignout && row.report_status === "Ready for Review" && (
        <section className="space-y-3 border border-amber-300 bg-amber-50/60 p-4" data-testid="pathologist-review-panel">
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">Pathologist review</h2>
          <p className="text-sm text-slate-700">
            Flagged ready by {row.transcribed_by_name || "transcription"} on {fmtDateTime(row.ready_at)}.
            Signing out locks the report and stops the TAT clock.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <Button onClick={signOut} disabled={busy === "signout" || !dx.trim()} data-testid="signout-report-button"
                    className="h-11 bg-[#134e4a] hover:bg-[#0f3e3a]">
              <PenLine size={15} className="mr-2" /> {busy === "signout" ? "Signing…" : "Review and sign out"}
            </Button>
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Return reason</Label>
              <Textarea value={returnReason} onChange={(e) => setReturnReason(e.target.value)} rows={2}
                        data-testid="return-reason-input" placeholder="What needs correcting?" className="mt-1 text-sm" />
            </div>
            <Button onClick={sendBack} disabled={busy === "return" || returnReason.trim().length < 3}
                    variant="outline" data-testid="return-report-button" className="h-11 border-slate-300">
              <Undo2 size={15} className="mr-2" /> Return to transcription
            </Button>
          </div>
        </section>
      )}

      {canSignout && locked && (
        <section className="space-y-3 border border-purple-300 bg-purple-50/50 p-4" data-testid="amendment-panel">
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">Amend signed-out report</h2>
          {!amending ? (
            <Button onClick={() => setAmending(true)} data-testid="start-amendment-button"
                    variant="outline" className="h-11 border-purple-400 text-purple-900 hover:bg-purple-100">
              <PenLine size={15} className="mr-2" /> File an amendment
            </Button>
          ) : (
            <>
              <p className="text-sm text-slate-700">
                Edit the final diagnosis above, then give the reason. The amendment logs the reason, who made it and when.
              </p>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Reason for amendment</Label>
              <Textarea value={amendReason} onChange={(e) => setAmendReason(e.target.value)} rows={3}
                        data-testid="amend-reason-input" placeholder="e.g. Corrected laterality after chart review"
                        className="text-sm" />
              <div className="flex flex-wrap gap-2">
                <Button onClick={fileAmendment} disabled={busy === "amend" || amendReason.trim().length < 5}
                        data-testid="file-amendment-button" className="h-11 bg-purple-700 hover:bg-purple-800">
                  {busy === "amend" ? "Filing…" : "File amended report"}
                </Button>
                <Button onClick={() => { setAmending(false); setAmendReason(""); }} variant="outline"
                        data-testid="cancel-amendment-button" className="h-11 border-slate-300">Cancel</Button>
              </div>
            </>
          )}
        </section>
      )}

      {(row.dictations || []).length > 0 && (
        <section className="border border-slate-200" data-testid="dictation-audio-panel">
          <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <Mic size={15} className="text-[#134e4a]" />
            <h2 className="text-base font-semibold text-slate-800 md:text-lg">Dictation audio</h2>
            <span className="ml-auto text-xs text-slate-500">Listen back before signing out</span>
          </header>
          <ul className="divide-y divide-slate-100">
            {row.dictations.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-3 px-3 py-3" data-testid={`dictation-${d.id}`}>
                <div className="min-w-[200px]">
                  <div className="text-sm font-semibold text-slate-900">{d.field_label}</div>
                  <div className="text-xs text-slate-500">{d.dictated_by_name} · {fmtDateTime(d.created_at)}</div>
                </div>
                <audio controls preload="none" className="h-9 min-w-[240px] flex-1"
                       data-testid={`dictation-audio-${d.id}`}
                       src={`${process.env.REACT_APP_BACKEND_URL}/api/dictations/${d.id}/audio`} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {(row.amendments || []).length > 0 && (
        <section className="border border-slate-200" data-testid="amendment-history">
          <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <History size={15} className="text-[#134e4a]" />
            <h2 className="text-base font-semibold text-slate-800 md:text-lg">Amendment history</h2>
          </header>
          <ul className="divide-y divide-slate-100">
            {row.amendments.map((a) => (
              <li key={a.version} className="px-3 py-3" data-testid={`amendment-v${a.version}`}>
                <div className="text-sm font-semibold text-slate-900">Version {a.version} — {a.by}</div>
                <div className="text-xs uppercase tracking-wide text-slate-500">{fmtDateTime(a.at)}</div>
                <div className="mt-1 text-sm text-slate-700">Reason: {a.reason}</div>
                {a.previous_diagnosis && (
                  <div className="mt-1 whitespace-pre-wrap border-l-2 border-slate-200 pl-2 font-mono text-xs text-slate-500">
                    {a.previous_diagnosis}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
